'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { MapPin } from 'lucide-react';
import Link from '@eliadmin/core/src/components/link';

import { BuyBoxVariants } from '@eliadmin/core/src/components/dlz/product-detail/buy-box/buy-box-variants';
import { CartRow } from '@eliadmin/core/src/components/dlz/product-detail/buy-box/cart-row';
import { PriceBlock } from '@eliadmin/core/src/components/dlz/product-detail/buy-box/price-block';
import { StockRow } from '@eliadmin/core/src/components/dlz/product-detail/buy-box/stock-row';
import { ProductOffers } from '@eliadmin/core/src/components/dlz/product-detail/extras/product-offers';
import { ProductPurchaseExtras } from '@eliadmin/core/src/components/dlz/product-detail/extras/product-purchase-extras';
import { AskForPriceButton } from '@eliadmin/core/src/components/product/ask-for-price-button';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

/**
 * What the shopper can do with the product: the status text of an ended / announced product, the
 * „ask for a price“ button (price on request), or price, stock and the cart row (hidden until a
 * variant is chosen).
 */
function Purchase({ dispatchNote }: { dispatchNote: string }) {
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  if (product.status === 'ended' || product.status === 'arrival') {
    return (
      <ProductStatusText
        status={product.status}
        sizeClassName="dlz-h3"
        className={product.status === 'ended' ? 'text-[#0F1012]' : 'text-[#9A5F00]'}
      />
    );
  }

  const isWaitingForVariant = !!product.variants?.length && !selectedVariant;
  if (!isWaitingForVariant && getProductPriceDisplay(selectedVariant ?? product).priceOnRequest) {
    return (
      <div className="[&>button]:w-full">
        <AskForPriceButton product={product} variant={selectedVariant} />
      </div>
    );
  }

  return (
    <>
      <PriceBlock />
      {isWaitingForVariant ? null : (
        <>
          <StockRow dispatchNote={dispatchNote} />
          <CartRow />
        </>
      )}
      <ProductPurchaseExtras />
    </>
  );
}

/** Everything between the highlights and the delivery box, in the order of the core `ProductPurchaseSection`. */
export function PurchaseSection({ dispatchNote }: { dispatchNote: string }) {
  return (
    <>
      <BuyBoxVariants />
      <ProductOffers />
      <Purchase dispatchNote={dispatchNote} />
      <Button asChild variant="outline" className="w-full whitespace-normal text-center">
        <Link href="/predajcovia">
          <MapPin className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Nájsť najbližšieho predajcu</span>
        </Link>
      </Button>
    </>
  );
}
